import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import './Page2.css';
import backgroundImage from '../images/page-2.jpg';
import backgroundMobile from '../images/page-2-mobile.jpg';
// === Tooltip-Positionierung wie in Page-1 (obj-fit:cover-aware) ===
const P2_ORIGINAL_W = 6000;  // <-- ggf. anpassen
const P2_ORIGINAL_H = 3374;  // <-- ggf. anpassen

function rectForCoverToPercent([x1, y1, x2, y2], containerW, containerH) {
    const scale = Math.max(containerW / P2_ORIGINAL_W, containerH / P2_ORIGINAL_H);
    const renderedW = P2_ORIGINAL_W * scale;
    const renderedH = P2_ORIGINAL_H * scale;
    const offsetX = (containerW - renderedW) / 2;
    const offsetY = (containerH - renderedH) / 2;

    const rx1 = x1 * scale + offsetX;
    const ry1 = y1 * scale + offsetY;
    const rw = (x2 - x1) * scale;
    const rh = (y2 - y1) * scale;

    return {
        left: `${(rx1 / containerW) * 100}%`,
        top: `${(ry1 / containerH) * 100}%`,
        width: `${(rw / containerW) * 100}%`,
        height: `${(rh / containerH) * 100}%`,
    };
}

// 1×1-Punkt (Startpunkt) pro Projekt im *Originalbild*
// 👉 Diese Werte einmalig einmessen (x,y) – reicht auch [x, y, x+1, y+1].
//    Ich setze dir brauchbare Platzhalter, damit es sofort läuft.
const TOOLTIP_RECTS_PX = {
    projekt1: [2450, 220, 2451, 221],   // TODO feinjustieren
    projekt2: [2450, 220, 2451, 221],   // TODO feinjustieren
    projekt3: [4420, 1160, 4421, 1161],  // TODO feinjustieren
    projekt4: [4420, 1160, 4421, 1161],  // TODO feinjustieren
};

 

// Feinoffset addieren (dx/dy in px → % des Containers)
const TOOLTIP_OFFSETS = {
    projekt1: { dx: 0, dy: 0 },
    projekt2: { dx: 0, dy: 0 },
    projekt3: { dx: 0, dy: 0 },
    projekt4: { dx: 0, dy: 0 },
};

function usePointerEnv() {
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);

    useEffect(() => {
        const hasTouchLike = () =>
            (typeof window !== 'undefined' && 'ontouchstart' in window) ||
            (typeof navigator !== 'undefined' &&
                ((navigator.maxTouchPoints ?? 0) > 0 ||
                    (navigator.msMaxTouchPoints ?? 0) > 0));

        let mq;
        if (typeof window !== 'undefined' && window.matchMedia) {
            mq = window.matchMedia('(pointer: coarse)');
        }

        const update = () => {
            setIsCoarsePointer((mq && mq.matches) || hasTouchLike());
        };

        update();

        const onChange = () => update();
        mq?.addEventListener?.('change', onChange);
        window.addEventListener?.('resize', onChange);
        window.addEventListener?.('touchstart', onChange, { passive: true });

        return () => {
            mq?.removeEventListener?.('change', onChange);
            window.removeEventListener?.('resize', onChange);
            window.removeEventListener?.('touchstart', onChange);
        };
    }, []);

    return { isCoarsePointer };
}

/* =========================
   DESKTOP — RENDER-STRUKTUR wie Page-3
========================= */
const areas = [
    {
        id: 'projekt1',
        clipPath:
            'polygon(12.1% 0.03%, 12.8% 99.63%, 32.95% 99.49%, 32.42% 61.85%, 38.6% 52.27%, 38.95% 0.47%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="red font-20 bold">eVolo Skyscraper</div>
                <div className="red font-25 extra-bold">Competition</div>
                <div className="black font-31">Naval Steel</div>
                <div className="black font-21">Structures turned</div>
                <div className="black font-39">Highrises</div>
                <div className="black font-39">Hamburg</div>
                <div className="black-bold font-24">www.evolo.us</div>
            </div>
        ),
    },
    {
        id: 'projekt2',
        clipPath:
            'polygon(60.23% 0%, 39.48% 0.15%, 39.13% 52.27%, 33.2% 61.99%, 33.65% 99.49%, 52.47% 98.93%, 52.73% 61.68%, 60.15% 52.87%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="heading-red">STAIRCASE APARTMENTS</div>
                <div className="subheading-black">Alexanderplatz Berlin</div>
                <div className="text-normal">Competition upon invitat-</div>
                <div className="text-normal-large">ion of www.plattformnach-</div>
                <div className="text-normal space">wuchsarchitekten.de</div>
                <div className="text-normal-small">
                    <span className="red-bold">Second prize.</span> Exhibitions:
                </div>
                <div className="text-normal">Berlin: Aedes Prenzlauer </div>
                <div className="text-normal spacing-025">Berg, K&ouml;ln: KAP-Forum</div>
            </div>
        ),
    },
    {
        id: 'projekt3',
        clipPath:
            'polygon(60.68% 38.01%, 69.38% 38.35%, 73.85% 53.79%, 73.75% 98.84%, 52.82% 99.34%, 52.9% 62.58%, 60.5% 52.94%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="red font-19">THE ROOSEVELT ISLAND</div>
                <div className="red font-17-8">UNIVERSAL ARTS CENTER</div>
                <div className="subheading-black">Manhattan, New York</div>
                <div className="black font-23-8">Competition upon invitat-</div>
                <div className="black font-22">ion of the AIANY Emerging</div>
                <div className="black font-22">New York Architects Com-</div>
                <div className="text-normal">mittee ENYA</div>
            </div>
        ),
    },
    {
        id: 'projekt4',
        clipPath:
            'polygon(84.53% 42.97%, 84.53% 99.34%, 74.28% 98.84%, 74.02% 42.71%)',
        labelHtml: (
            <div className="tooltip-content">
                <div className="heading-red">THE SPATIAL APERTURE</div>
                <div className="black font-56">Furniture</div>
                <div className="black font-27">Convertible Seat and</div>
                <div className="black font-25-spacing">Shell controls opening</div>
                <div className="black font-25-extra-space">and turns seat into</div>
                <div className="black font-27">lounger</div>
            </div>
        ),
    },
];
function Desktop() {
    const [hoveredId, setHoveredId] = useState(null);

    const wrapperRef = useRef(null);
    const [labelBoxes, setLabelBoxes] = useState({});

    const wrapper = {
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        cursor: 'default',
        paddingTop: '3em',  
    };

    const imgStyle = {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        pointerEvents: 'none',
    };

   
    useLayoutEffect(() => {
        const el = wrapperRef.current;
        if (!el) return;

        const recompute = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;

            // 1) Bild-Skalierung (wie gehabt, für die Positionierung)
            const imgScale = Math.max(w / P2_ORIGINAL_W, h / P2_ORIGINAL_H);

            // 2) EINFACHE, STUFIGE SCALE-LOGIK NACH BREITE
            let labelScale;
            if (w >= 2600) {
                // großer iMac
                labelScale = 1.2;
            } else if (w >= 2000) {
                labelScale = 1.0;
            } else if (w >= 1600) {
                labelScale = 0.75;
            } else if (w >= 1300) {
                // dein Bereich (ca. 1380px): bewusst kleiner
                labelScale = 0.55;
            } else {
                labelScale = 0.45;   // sehr kleine Fenster
            }

            //console.log("labelScale:", labelScale.toFixed(2), "w:", w, "h:", h);

            // 3) Label-Breite leicht an Bildgröße koppeln
            const baseLabelWidth = 320;
            const labelWidthPx = Math.min(Math.max(baseLabelWidth * imgScale, 270), 420);

            const next = {};
            Object.entries(TOOLTIP_RECTS_PX).forEach(([id, rectPx]) => {
                const base = rectForCoverToPercent(rectPx, w, h);

                const { dx = 0, dy = 0 } = TOOLTIP_OFFSETS[id] || {};
                const dxPx = dx * imgScale;
                const dyPx = dy * imgScale;

                const leftPct = parseFloat(base.left);
                const topPct = parseFloat(base.top);

                next[id] = {
                    position: 'absolute',
                    left: `calc(${leftPct}% + ${dxPx}px)`,
                    top: `calc(${topPct}% + ${dyPx}px)`,

                    width: `${labelWidthPx}px`,
                    maxWidth: '30vw',
                    minWidth: '240px',

                    pointerEvents: 'none',
                    transformOrigin: 'top left',
                    transform: `scale(${labelScale})`,
                    willChange: 'transform',
                };
            });

            setLabelBoxes(next);
        };

        recompute();

        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(recompute);
            ro.observe(el);
        } else {
            window.addEventListener('resize', recompute);
        }
        return () => {
            if (ro) ro.disconnect();
            else window.removeEventListener('resize', recompute);
        };
    }, []);


    return (
        <div ref={wrapperRef} style={wrapper}>
            <img src={backgroundImage} alt="Page 2" style={imgStyle} />

            {/* Hover-Flächen (bleiben wie gehabt) */}
            {areas.map((area) => (
                <div
                    key={area.id}
                    className={`hover-area ${hoveredId === area.id ? 'hovered' : ''}`}
                    style={{ clipPath: area.clipPath }}
                    onMouseEnter={() => setHoveredId(area.id)}
                    onMouseLeave={() => setHoveredId(null)}
                />
            ))}

            {/* Labels: dynamisch positioniert – kein viewport-basiertes top/left mehr */}
            {areas.map((area) =>
                hoveredId === area.id ? (
                    <div
                        key={`${area.id}-label`}
                        className={`hover-label ${area.id}`}
                        style={labelBoxes[area.id]}
                    >
                        {area.labelHtml}
                    </div>
                ) : null
            )}
        </div>
    );
}



/* =========================
   MOBILE —  (rechts starten, feste Pixel-Areas, ready-Delay)
========================= */

// Mobile Bild-Maße (intrinsic)
const MOBILE = { width: 1536, height: 1096 };

// Mobile-Areas (fixe Pixel-Koordinaten)
const mobileAreas = [
    { id: 'projekt1', shape: 'poly', coords: '0,1096,446,1094,438,680,574,582,601,56,0,54' },
    { id: 'projekt2', shape: 'poly', coords: '985,580,844,687,837,1098,448,1094,442,680,577,581,609,57,990,54' },
    { id: 'projekt3', shape: 'poly', coords: '989,579,990,473,1153,469,1197,535,1194,1093,843,1098,844,691' },
    { id: 'projekt4', shape: 'rect', coords: '1201,1091,1483,531' },
];

function parseCoords(coordsStr) {
    return coordsStr.split(',').map((n) => Number(n.trim()));
}
function normRect([x1, y1, x2, y2]) {
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const right = Math.max(x1, x2);
    const bottom = Math.max(y1, y2);
    return [left, top, right, bottom];
}

function Mobile() {
    const [selectedId, setSelectedId] = useState(null);
    const [ready, setReady] = useState(false);
    const containerRef = useRef(null);
    const [renderSize, setRenderSize] = useState({ w: MOBILE.width, h: MOBILE.height });
    const didInitialScroll = useRef(false);

    // Größe berechnen & initial rechts starten
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const compute = () => {
            const h = el.clientHeight; // 100vh
            const scale = h / MOBILE.height;
            const w = Math.round(MOBILE.width * scale);
            setRenderSize({ w, h });

            if (!didInitialScroll.current) {
                const RIGHT_OFFSET = 0;
                el.scrollLeft = Math.max(0, w - el.clientWidth - RIGHT_OFFSET);
                didInitialScroll.current = true;
            }
        };

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // kurzes Post-Mount-Delay, damit Doppeltipp von Page-1 nicht „durchschlägt“
    useEffect(() => {
        const t = setTimeout(() => setReady(true), 400);
        return () => clearTimeout(t);
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100vw',
                height: '100vh',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                position: 'relative',
                background: '#000',
            }}
            onClick={(e) => {
                // Tap aufs Bild leert das Label (App.js fängt Double-Tap)
                if (!(e.target instanceof HTMLAreaElement)) setSelectedId(null);
            }}
        >
            <img
                src={backgroundMobile}
                alt="Page 2 (mobile)"
                useMap="#page2-mobile-map"
                width={renderSize.w}
                height={renderSize.h}
                style={{ height: '100%', width: 'auto', display: 'block' }}
                fetchPriority="high"
                decoding="async"
                draggable={false}
            />


            <map name="page2-mobile-map">
                {mobileAreas.map((a) => {
                    const scale = renderSize.h / MOBILE.height;
                    let coordsStr;
                    if (a.shape === 'rect') {
                        const [x1, y1, x2, y2] = normRect(parseCoords(a.coords));
                        coordsStr = [x1, y1, x2, y2].map((n) => Math.round(n * scale)).join(',');
                    } else {
                        coordsStr = parseCoords(a.coords).map((n) => Math.round(n * scale)).join(',');
                    }
                    return (
                        <area
                            key={a.id}
                            shape={a.shape}
                            coords={coordsStr}
                            alt={`${a.id} – Details`}
                            aria-label={`${a.id} – Details`}
                            role="button"
                            onTouchEnd={() => {
                                if (!ready) return;
                                setSelectedId(a.id);
                            }}
                            onClick={(e) => {
                                if (!ready) return;
                                e.preventDefault();
                                setSelectedId(a.id);
                            }}
                        />
                    );
                })}
            </map>

            {selectedId && (
                <div
                    className={`hover-label mobile ${selectedId}`}
                    style={{
                        position: 'fixed',
                        left: '-16px',
                        top: '10px',
                        transform: 'scale(0.65) translate(-10%, -20%)',
                        maxWidth: '86vw',
                        background: '#fff',
                        color: '#000',
                        padding: '12px 14px',
                        borderRadius: 8,
                        lineHeight: 1.25,

                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {(areas.find((a) => a.id === selectedId) || {}).labelHtml || (
                        <div style={{ fontWeight: 700 }}>{selectedId}</div>
                    )}
                </div>
            )}
        </div>
    );
}
function DesktopTablet() {
    const [hoveredId, setHoveredId] = useState(null);
    const containerRef = useRef(null);
    const [state, setState] = useState({ w: 0, h: 0, scale: 1 });
    const [labelBoxes, setLabelBoxes] = useState({});

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const recompute = () => {
            const h = el.clientHeight;                   // 100vh
            const scale = h / P2_ORIGINAL_H;             // Höhe fix, Breite skaliert
            const w = Math.round(P2_ORIGINAL_W * scale); // → Scrollbreite

            setState({ w, h, scale });

            // initial nach rechts scrollen
            el.scrollLeft = Math.max(0, w - el.clientWidth);

            // Label-Scale: Breite & Höhe berücksichtigen, dann clampen
            const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
            const designW = 1600, designH = 900;
            const widthFactor = el.clientWidth / designW;
            const heightFactor = el.clientHeight / designH;
            const rawLabelScale = Math.min(widthFactor, heightFactor) * 0.55;
            //const labelScale = clamp(rawLabelScale, 0.55, 0.95);
            // Standard für „normale“ Breiten (Tablet / kleiner Desktop)
            let BASE_MULTIPLIER = 0.55;
            let MIN_SCALE = 0.55;
            let MAX_SCALE = 0.95;

            // 👇 EXTRA: < 768 px → alles kleiner skalieren
            if (window.innerWidth < 768) {
                BASE_MULTIPLIER = 0.45; // insgesamt kleiner
                MIN_SCALE = 0.40;
                MAX_SCALE = 0.80;
            }

            const labelScale = clamp(rawLabelScale * BASE_MULTIPLIER, MIN_SCALE, MAX_SCALE);

            const baseLabelWidth = 270;
            const labelWidthPx = clamp(baseLabelWidth * scale, 270, 420);

            const next = {};
            Object.entries(TOOLTIP_RECTS_PX).forEach(([id, rectPx]) => {
                const [x1, y1] = rectPx;
                const { dx = 0, dy = 0 } = TOOLTIP_OFFSETS[id] || {};
                const leftPx = (x1 + dx) * scale;
                const topPx = (y1 + dy) * scale;

                next[id] = {
                    position: 'absolute',
                    left: `${Math.round(leftPx)}px`,
                    top: `${Math.round(topPx)}px`,
                    width: `${Math.round(labelWidthPx)}px`,
                    pointerEvents: 'none',
                    transformOrigin: 'top left',
                    transform: `scale(${labelScale})`,
                    willChange: 'transform',
                };
            });

            setLabelBoxes(next);
        };

        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const activate = (id) => setHoveredId(id);
    const deactivate = () => setHoveredId(null);

    return (
        <div
            ref={containerRef}
            style={{
                width: '100vw',
                height: '100vh',
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-x',
                position: 'relative',
                background: '#000',
            }}
            onMouseLeave={deactivate}
            onClick={(e) => {
                if (!(e.target instanceof HTMLElement)) return;
                if (!e.target.closest('.hover-area')) setHoveredId(null);
            }}
        >
            <div style={{ position: 'relative', width: `${state.w}px`, height: '100%' }}>
                <img
                    src={backgroundImage}
                    alt="Page 2"
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: 'auto',
                        display: 'block',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                    fetchPriority="high"
                    decoding="async"
                    draggable={false}
                />

                {areas.map((area) => (
                    <div
                        key={area.id}
                        className={`hover-area ${hoveredId === area.id ? 'hovered' : ''}`}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            clipPath: area.clipPath,
                        }}
                        onMouseEnter={() => activate(area.id)}
                        onMouseLeave={deactivate}
                        onTouchEnd={() => activate(area.id)}
                        onClick={(e) => { e.preventDefault(); activate(area.id); }}
                    />
                ))}

                {/*{hoveredId && (() => {*/}
                {/*    const area = areas.find(a => a.id === hoveredId);*/}
                {/*    if (!area) return null;*/}

                {/*    // 📌 TOUCH (coarse pointer): Tooltip FIXED oben links*/}
                {/*    if (isCoarsePointer) {*/}
                {/*        return (*/}
                {/*            <div*/}
                {/*                className={`hover-label mobile tablet-fixed ${hoveredId}`}*/}
                {/*                style={{*/}
                {/*                    position: 'fixed',*/}
                {/*                    left: '8px',*/}
                {/*                    top: '8px',*/}
                {/*                    transform: 'scale(0.72)',   // ggf. anpassen*/}
                {/*                    maxWidth: '86vw',*/}
                {/*                    background: '#fff',*/}
                {/*                    color: '#000',*/}
                {/*                    padding: '12px 14px',*/}
                {/*                    borderRadius: 8,*/}
                {/*                    lineHeight: 1.25,*/}
                {/*                    zIndex: 50,*/}
                {/*                    pointerEvents: 'auto',*/}
                {/*                }}*/}
                {/*            >*/}
                {/*                {area.labelHtml}*/}
                {/*            </div>*/}
                {/*        );*/}
                {/*    }*/}

                {/*    // 🖱 MAUS: wie bisher relativ zum Bild*/}
                {/*    return (*/}
                {/*        <div className={`hover-label ${hoveredId}`} style={labelBoxes[hoveredId]}>*/}
                {/*            {area.labelHtml}*/}
                {/*        </div>*/}
                {/*    );*/}
                {/*})()}*/}
                {hoveredId && (
                    <div className={`hover-label ${hoveredId}`} style={labelBoxes[hoveredId]}>
                        {(areas.find(a => a.id === hoveredId) || {}).labelHtml}
                    </div>
                )}
            </div>
        </div>
    );
}

//export default function Page2() {
//    const [vw, setVw] = React.useState(
//        () => (typeof window !== 'undefined' ? window.innerWidth : 1920)
//    );

//    const { isCoarsePointer } = usePointerEnv();

//    React.useEffect(() => {
//        const onResize = () => setVw(window.innerWidth);
//        window.addEventListener('resize', onResize);
//        return () => window.removeEventListener('resize', onResize);
//    }, []);

//    const isNarrow = vw < 768;
//    const isTabletBand = vw >= 768 && vw <= 1024;

//    // --------------------------------------------
//    //   < 768 px:
//    //   - Touch  → Mobile (Scroll + fixed Tooltip)
//    //   - Maus   → DesktopTablet (Scroll + Tooltip relativ zum Bild)
//    // --------------------------------------------
//    if (isNarrow) {
//        if (isCoarsePointer) {
//            return <Mobile />;
//        } else {
//            return <DesktopTablet isCoarsePointer={false} />;
//            // Maus → wie Tablet, scrollt horizontal
//        }
//    }

//    // --------------------------------------------
//    //   768 – 1024 → DesktopTablet
//    // --------------------------------------------
//    if (isTabletBand) {
//        return <DesktopTablet isCoarsePointer={isCoarsePointer} />;
//    }

//    // --------------------------------------------
//    //   > 1024 → klassischer Desktop
//    // --------------------------------------------
//    return <Desktop />;
//}
export default function Page2() {
    const [vw, setVw] = React.useState(
        () => (typeof window !== 'undefined' ? window.innerWidth : 1920)
    );

    const { isCoarsePointer } = usePointerEnv();

    React.useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const isNarrow = vw < 768;
    const isTabletBand = vw >= 768 && vw <= 1024;

    // < 768 px:
    //   - Touch  → Mobile (Scroll + fixed Tooltip oben links)
    //   - Maus   → DesktopTablet (Scroll + bild-relative Tooltips)
    if (isNarrow) {
        if (isCoarsePointer) {
            return <Mobile />;
        } else {
            return <DesktopTablet />;
        }
    }

    // 768 – 1024 → immer DesktopTablet (egal ob Touch oder Maus)
    if (isTabletBand) {
        return <DesktopTablet />;
    }

    // > 1024 → klassischer Desktop (voller Screen, Hover-Tooltips)
    return <Desktop />;
}
